import React, { useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import 'moment/locale/ja'
import moment from 'moment'

// constants
import { listHeaderGrayStyle } from '@/constants/layout'

// components
import { Button } from '@/components/atoms/button'
import { DateRangeField } from '@/components/molecules/date-range-field'
import { DateField } from '@/components/molecules/date-field'
import { SubjectSelect } from '@/components/molecules/customer/sales-manager/usage-detail/subject-select'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'

// models
import { SalesSubjectMasterType, SalesSubSubjectMasterType, SalesDetailSearchType } from '@/models/sales-manager/sales'

type MasterOption = {
  id: string
  name: string
}

type ContainerProps = {
  searchContent: SalesDetailSearchType
  onClear: () => void
  onSearch: (values: SalesDetailSearchType) => void
  salesSubjectMasters: SalesSubjectMasterType[]
  salesPaymentOptions: MasterOption[]
  salesAccountsReceivableOptions: MasterOption[]
  salesDiscountOptions: MasterOption[]
}

export const DetailedSearchPanel: React.FC<ContainerProps> = props => {
  const { t, i18n } = useTranslation()
  const { salesSubjectMasters, salesPaymentOptions, salesAccountsReceivableOptions, salesDiscountOptions } = props

  // カレンダーを日本語表示にする
  useEffect(() => {
    moment.locale(i18n.language === 'ja' ? 'ja' : 'en')
  }, [i18n.language])

  // フォーム用の型（売上金額はstringで管理）
  type FormValues = Omit<SalesDetailSearchType, 'minSalesPrice' | 'maxSalesPrice'> & {
    minSalesPrice?: string | number
    maxSalesPrice?: string | number
  }

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormValues & { beginDate?: string; endDate?: string }>({
    defaultValues: {
      ...(props.searchContent || {
        unpaidType: '',
        beginPaymentHour: '00',
        beginPaymentMinute: '00',
        endPaymentHour: '23',
        endPaymentMinute: '59',
      }),
      beginDate: props.searchContent?.beginDate || '',
      endDate: props.searchContent?.endDate || '',
      salesSubjectMasterId: props.searchContent?.salesSubjectMasterId || '',
      salesSubSubjectId: props.searchContent?.salesSubSubjectId || '',
      salesDiscountId: props.searchContent?.salesDiscountId || '',
      minSalesPrice: props.searchContent?.minSalesPrice ? String(props.searchContent.minSalesPrice) : undefined,
      maxSalesPrice: props.searchContent?.maxSalesPrice ? String(props.searchContent.maxSalesPrice) : undefined,
    },
  })

  const handleClear = () => {
    // 利用明細日付はクリアしない（期間なしの検索を防ぐため）、それ以外は全てクリア
    const currentBeginDate = watch('beginDate')
    const currentEndDate = watch('endDate')
    reset({
      beginDate: currentBeginDate || '',
      endDate: currentEndDate || '',
      unpaidType: '',
      salesSubjectMasterId: '',
      salesSubSubjectId: '',
      minSalesPrice: '',
      maxSalesPrice: '',
      salesDiscountId: '',
      paymentMethodName: '',
      accountsReceivableName: '',
      beginPaymentDate: '',
      beginPaymentHour: '00',
      beginPaymentMinute: '00',
      endPaymentDate: '',
      endPaymentHour: '23',
      endPaymentMinute: '59',
    })
    props.onClear()
  }

  const onSubmit = (values: FormValues & { beginDate?: string; endDate?: string }) => {
    props.onSearch({
      ...values,
      beginDate: values.beginDate || undefined,
      endDate: values.endDate || undefined,
      minSalesPrice: values.minSalesPrice ? Number(values.minSalesPrice) : undefined,
      maxSalesPrice: values.maxSalesPrice ? Number(values.maxSalesPrice) : undefined,
    })
  }

  // salesSubSubjectId または salesSubjectMasterId から表示用の defaultValue を取得
  const getSubjectDefaultValue = () => {
    const salesSubSubjectId = watch('salesSubSubjectId')
    const salesSubjectMasterId = watch('salesSubjectMasterId')
    // 補助科目指定時
    if (salesSubSubjectId) {
      for (const master of salesSubjectMasters) {
        const sub = master.subSubjects?.find(s => s.id === salesSubSubjectId)
        if (sub) {
          return {
            salesSubjectId: master.id,
            salesSubjectName: master.subjectName,
            salesSubSubjectId,
            salesSubSubjectName: sub.subSubjectName,
          }
        }
      }
    }
    // 科目のみ指定時（従来の salesSubjectMasterId のみのURL互換含む）
    if (!salesSubjectMasterId) return undefined
    for (const master of salesSubjectMasters) {
      if (master.id === salesSubjectMasterId) {
        return {
          salesSubjectId: salesSubjectMasterId,
          salesSubjectName: master.subjectName,
          salesSubSubjectId: '',
          salesSubSubjectName: '',
        }
      }
      for (const sub of master.subSubjects || []) {
        if (sub.id === salesSubjectMasterId) {
          return {
            salesSubjectId: master.id,
            salesSubjectName: master.subjectName,
            salesSubSubjectId: salesSubjectMasterId,
            salesSubSubjectName: sub.subSubjectName,
          }
        }
      }
    }
    return undefined
  }

  // 全角数字を半角数字に変換
  const convertFullWidthToHalfWidth = (value: string): string => {
    return value.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
  }

  // 時間オプション (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
  // 分オプション (00-59)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

  return (
    <div css={modalWrapperStyle}>
      {/* 一行目: 利用明細日付 未収金 売上 */}
      <div css={modalInputWrapperStyle}>
        {/* 利用明細日付 */}
        <div css={[inputFieldStyle, { flex: 1, maxWidth: 'fit-content' }]}>
          <div css={labelStyle}>{t('Sales date')}</div>
          <Controller
            name="beginDate"
            control={control}
            render={({ field: beginDateField }) => (
              <Controller
                name="endDate"
                control={control}
                render={({ field: endDateField }) => (
                  <DateRangeField
                    beginDate={beginDateField.value || ''}
                    endDate={endDateField.value || ''}
                    changeRangeDate={(beginDate, endDate) => {
                      beginDateField.onChange(beginDate)
                      endDateField.onChange(endDate)
                    }}
                    useSalesManagerBehavior={true}
                  />
                )}
              />
            )}
          />
        </div>

        {/* 未収金 */}
        <div css={[inputFieldStyle, { maxWidth: 152, flex: 0.5, position: 'relative' }]}>
          <div css={labelStyle}>{t('Amount to be collected')}</div>
          <Controller
            name="unpaidType"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || ''}
                onChange={value => field.onChange(String(value) as '' | '1' | '0')}
                customStyle={css({ width: '100%', paddingBottom: 0 })}
                getPopupContainer={triggerNode => triggerNode.parentElement || document.body}
              >
                <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                <AntSelect.Option value="1">{t('Exist')}</AntSelect.Option>
                <AntSelect.Option value="0">{t('None')}</AntSelect.Option>
              </Select>
            )}
          />
        </div>

        {/* 売上（右端を売掛先に合わせるため flex:1、入力は少し長め） */}
        <div css={[inputFieldStyle, { flex: 1, minWidth: 0 }]}>
          <div css={labelStyle}>{t('Earnings')}</div>
          <div css={rangeInputStyle}>
            <Controller
              name="minSalesPrice"
              control={control}
              render={({ field }) => (
                <input
                  css={earningsInputStyle}
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={field.value ?? ''}
                  onChange={e => {
                    field.onChange(convertFullWidthToHalfWidth(e.target.value))
                  }}
                />
              )}
            />
            <span css={wavyLineStyle}>~</span>
            <Controller
              name="maxSalesPrice"
              control={control}
              render={({ field }) => (
                <input
                  css={earningsInputStyle}
                  type="text"
                  inputMode="numeric"
                  placeholder="50000"
                  value={field.value ?? ''}
                  onChange={e => {
                    field.onChange(convertFullWidthToHalfWidth(e.target.value))
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* 二行目: 科目/補助科目 割引科目 支払い方法 売掛先 */}
      <div css={modalInputWrapperStyle}>
        {/* 科目/補助科目（他項目の約1.25倍幅） */}
        <div css={[inputFieldStyle, { maxWidth: '100%', flex: 1.25, position: 'relative' }]}>
          <div css={labelStyle}>{t('SubjectAndSubSubject')}</div>
          <Controller
            name="salesSubjectMasterId"
            control={control}
            render={({ field }) => (
              <SubjectSelect
                salesSubjectMasters={salesSubjectMasters}
                isEditMode={false}
                onChange={(value: { subjectMaster: SalesSubjectMasterType; subSubject: SalesSubSubjectMasterType | undefined }) => {
                  if (value.subSubject) {
                    setValue('salesSubjectMasterId', '')
                    setValue('salesSubSubjectId', value.subSubject.id)
                  } else {
                    setValue('salesSubjectMasterId', value.subjectMaster.id)
                    setValue('salesSubSubjectId', '')
                  }
                  field.onChange(value.subSubject?.id || value.subjectMaster.id)
                }}
                defaultValue={getSubjectDefaultValue()}
              />
            )}
          />
        </div>

        {/* 割引科目（他3つと同幅） */}
        <div css={[inputFieldStyle, { maxWidth: '100%', flex: 1 }]}>
          <div css={labelStyle}>{t('Discount subject')}</div>
          <Controller
            name="salesDiscountId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || ''}
                onChange={value => field.onChange(String(value))}
                customStyle={css({ width: '100%', paddingBottom: 0 })}
                getPopupContainer={triggerNode => triggerNode.parentElement || document.body}
              >
                <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                {salesDiscountOptions.map(option => (
                  <AntSelect.Option key={option.id} value={option.id}>
                    {option.name}
                  </AntSelect.Option>
                ))}
              </Select>
            )}
          />
        </div>

        {/* 支払い方法（他3つと同幅） */}
        <div css={[inputFieldStyle, { maxWidth: '100%', flex: 1 }]}>
          <div css={labelStyle}>{t('Method of payment')}</div>
          <Controller
            name="paymentMethodName"
            control={control}
            render={({ field }) => {
              // 予約詳細と同じく Option value は id（name だと同名で検索が壊れる）
              // 検索条件は名称のため、選択時に name へ変換する
              const selectedId = salesPaymentOptions.find(option => option.name === field.value)?.id ?? ''

              return (
                <AntSelect
                  showSearch
                  optionFilterProp="children"
                  filterOption={true}
                  style={{ width: '100%' }}
                  suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
                  value={selectedId}
                  onChange={selectedValue => {
                    if (!selectedValue) {
                      field.onChange('')
                      return
                    }
                    const option = salesPaymentOptions.find(item => item.id === selectedValue)
                    field.onChange(option?.name ?? '')
                  }}
                >
                  <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                  {salesPaymentOptions.map((option, index) => (
                    <AntSelect.Option key={index} value={option.id}>
                      {option.name}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
              )
            }}
          />
        </div>

        {/* 売掛先（他3つと同幅） */}
        <div css={[inputFieldStyle, { maxWidth: '100%', flex: 1 }]}>
          <div css={labelStyle}>{t('Accounts receivable')}</div>
          <Controller
            name="accountsReceivableName"
            control={control}
            render={({ field }) => {
              const selectedId = salesAccountsReceivableOptions.find(option => option.name === field.value)?.id ?? ''

              return (
                <AntSelect
                  showSearch
                  optionFilterProp="children"
                  filterOption={true}
                  style={{ width: '100%' }}
                  suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
                  value={selectedId}
                  onChange={selectedValue => {
                    if (!selectedValue) {
                      field.onChange('')
                      return
                    }
                    const option = salesAccountsReceivableOptions.find(item => item.id === selectedValue)
                    field.onChange(option?.name ?? '')
                  }}
                >
                  <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                  {salesAccountsReceivableOptions.map((option, index) => (
                    <AntSelect.Option key={index} value={option.id}>
                      {option.name}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
              )
            }}
          />
        </div>
      </div>

      <div css={modalInputWrapperStyle}>
        {/* 入金日時 */}
        <div css={[inputFieldStyle, { maxWidth: '100%' }]}>
          <div css={labelStyle}>{t('Payment Date')}</div>
          <div css={paymentDateRangeStyle}>
            {/* 開始日時 */}
            <div css={dateTimeFieldStyle}>
              <Controller
                name="beginPaymentDate"
                control={control}
                render={({ field: beginDateField }) => (
                  <DateField
                    value={beginDateField.value || null}
                    onChange={date => {
                      beginDateField.onChange(date ? date.format('YYYY-MM-DD') : '')
                    }}
                    displayFormat="YYYY/MM/DD"
                    placeholder={t('Date')}
                    openDirection="up"
                    id="beginPaymentDate"
                    customCss={css({
                      marginRight: 8,
                      'div.SingleDatePicker': {
                        '.SingleDatePickerInput': {
                          'div.SingleDatePicker_picker': {
                            'div.DayPicker': {
                              // 日付フィールドの半分（約16px）＋HH:MMの高さ（約32px）分上に被せる
                              top: -48,
                              left: 40,
                            },
                          },
                        },
                      },
                    })}
                  />
                )}
              />
              <Controller
                name="beginPaymentHour"
                control={control}
                render={({ field }) => (
                  <Select
                    customStyle={css({ width: 70, paddingBottom: 0, marginRight: 4 })}
                    value={field.value || '00'}
                    onChange={value => field.onChange(String(value))}
                  >
                    {hourOptions.map(h => (
                      <AntSelect.Option key={h} value={h}>
                        {h}
                      </AntSelect.Option>
                    ))}
                  </Select>
                )}
              />
              <span>:</span>
              <Controller
                name="beginPaymentMinute"
                control={control}
                render={({ field }) => (
                  <Select
                    customStyle={css({ width: 70, paddingBottom: 0, marginLeft: 4 })}
                    value={field.value || '00'}
                    onChange={value => field.onChange(String(value))}
                  >
                    {minuteOptions.map(m => (
                      <AntSelect.Option key={m} value={m}>
                        {m}
                      </AntSelect.Option>
                    ))}
                  </Select>
                )}
              />
            </div>
            <span css={wavyLineStyle}>~</span>
            {/* 終了日時 */}
            <div css={dateTimeFieldStyle}>
              <Controller
                name="endPaymentDate"
                control={control}
                render={({ field: endDateField }) => (
                  <DateField
                    value={endDateField.value || null}
                    onChange={date => {
                      endDateField.onChange(date ? date.format('YYYY-MM-DD') : '')
                    }}
                    displayFormat="YYYY/MM/DD"
                    placeholder={t('Date')}
                    openDirection="up"
                    id="endPaymentDate"
                    customCss={css({
                      marginRight: 8,
                      'div.SingleDatePicker': {
                        '.SingleDatePickerInput': {
                          'div.SingleDatePicker_picker': {
                            'div.DayPicker': {
                              // 日付フィールドの半分（約16px）＋HH:MMの高さ（約32px）分上に被せる
                              top: -48,
                              left: 10,
                            },
                          },
                        },
                      },
                    })}
                  />
                )}
              />
              <Controller
                name="endPaymentHour"
                control={control}
                render={({ field }) => (
                  <Select
                    customStyle={css({ width: 70, paddingBottom: 0, marginRight: 4 })}
                    value={field.value || '23'}
                    onChange={value => field.onChange(String(value))}
                  >
                    {hourOptions.map(h => (
                      <AntSelect.Option key={h} value={h}>
                        {h}
                      </AntSelect.Option>
                    ))}
                  </Select>
                )}
              />
              <span>:</span>
              <Controller
                name="endPaymentMinute"
                control={control}
                render={({ field }) => (
                  <Select
                    customStyle={css({ width: 70, paddingBottom: 0, marginLeft: 4 })}
                    value={field.value || '59'}
                    onChange={value => field.onChange(String(value))}
                  >
                    {minuteOptions.map(m => (
                      <AntSelect.Option key={m} value={m}>
                        {m}
                      </AntSelect.Option>
                    ))}
                  </Select>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div css={searchButtonStyle}>
        <Button buttonType={3} width={92} height={32} fontSize={12} marginRight={16} onClick={handleClear}>
          {t('Clear')}
        </Button>
        <Button buttonType={1} width={92} height={32} fontSize={12} onClick={handleSubmit(onSubmit)}>
          {t('Search')}
        </Button>
      </div>
    </div>
  )
}

const modalWrapperStyle = css(listHeaderGrayStyle, {
  width: 'calc(100% - 48px)',
  height: 'auto',
  minWidth: 'auto',
  background: '#fff',
  position: 'absolute',
  top: 90,
  border: '1px solid #F2F2F2',
  boxShadow: '0px 3px 4px #0000001A',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
  // listHeaderGrayStyle の fontWeight: bold を打ち消し（科目/補助科目のplaceholder等が太字になるのを防ぐ）
  fontWeight: 'normal',
})

const modalInputWrapperStyle = css({
  display: 'flex',
  flexDirection: 'row',
  gap: 24,
  padding: 24,
  border: '1px solid #F2F2F2',
})

const inputFieldStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxWidth: 304,
  width: '100%',
})

const labelStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
})

const inputStyle = css({
  padding: '8px 15px',
  height: 32,
  width: 120,
  border: '1px solid #CCCCCC',
  borderRadius: 19,
  fontSize: 14,
  '::placeholder': {
    color: '#9ca3af',
    fontSize: 14,
  },
})

// 売上範囲用
const earningsInputStyle = css(inputStyle, {
  width: 140,
})

const inputTextStyle = css({
  padding: '8px 15px',
  height: 32,
  width: '100%',
  border: '1px solid #CCCCCC',
  borderRadius: 4,
  fontSize: 14,
  '::placeholder': {
    color: '#9ca3af',
    fontSize: 14,
  },
})

const rangeInputStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: 32,
})

const paymentDateRangeStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
})

const dateTimeFieldStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

const wavyLineStyle = css({
  paddingInline: 7,
})

const searchButtonStyle = css({
  display: 'flex',
  padding: '16px 32px',
})
